import { CreateUserRequestV1, DeleteMode, UserFormModel, UserResponseV1 } from '@access-management/access-management';
import { DeleteEntityDialog } from '@access-management/delete-entity-dialog/delete-entity-dialog';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, FormField, required } from '@angular/forms/signals';
import { RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';

import { DialogMode } from '@core/models/models';
import { AccessManagementService } from '@core/services/access-management';
import { MembershipService } from '@core/services/membership';

import {
  AccountMembershipResponseV1,
  OptionResponseV1,
  PackageResponseV1,
  UserMembershipSummary
} from '../../membership/membership';

@Component({
  selector: 'app-users-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FormField,
    RouterLink,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    DialogModule,
    InputTextModule,
    ProgressSpinnerModule,
    SelectModule,
    DeleteEntityDialog
  ],
  templateUrl: './users-table.html',
  styleUrl: './users-table.scss'
})
export class UsersTableComponent {
  private readonly accessManagementService = inject(AccessManagementService);
  private readonly membershipService = inject(MembershipService);
  private readonly messageService = inject(MessageService);

  readonly usersResource = this.accessManagementService.createUsersResource();
  readonly accountsResource = this.accessManagementService.createAccountsResource();
  readonly packagesResource = this.membershipService.createPackagesResource();
  readonly optionsResource = this.membershipService.createOptionsResource();
  readonly searchTerm = signal<string>('');
  readonly showDialog = signal<boolean>(false);
  readonly dialogMode = signal<DialogMode>(DialogMode.Create);
  readonly selectedUserId = signal<string | null>(null);
  readonly deletingId = signal<string | null>(null);
  readonly showDeleteDialog = signal<boolean>(false);
  readonly userPendingDelete = signal<UserResponseV1 | null>(null);
  readonly saving = signal<boolean>(false);
  readonly showMembershipsDialog = signal<boolean>(false);
  readonly selectedUserForMemberships = signal<UserResponseV1 | null>(null);
  readonly selectedAccountId = signal<string>('');
  readonly membershipsLoading = signal<boolean>(false);
  readonly accountMembershipSummary = signal<AccountMembershipResponseV1 | null>(null);
  readonly showAllocateDialog = signal<boolean>(false);
  readonly selectedPackageId = signal<string>('');
  readonly selectedOptionId = signal<string>('');
  readonly allocating = signal<boolean>(false);

  readonly userFormModel = signal<UserFormModel>({
    externalId: '',
    email: '',
    name: '',
    isActive: true
  });

  readonly userForm = form(this.userFormModel, (schemaPath) => {
    required(schemaPath.externalId, { message: 'External ID is required' });
    required(schemaPath.email, { message: 'Email is required' });
    required(schemaPath.name, { message: 'Name is required' });
  });

  readonly columns = signal<{ field: string; header: string; sortable: boolean }[]>([
    { field: 'name', header: 'Name', sortable: true },
    { field: 'email', header: 'Email', sortable: true },
    { field: 'externalId', header: 'External ID', sortable: false },
    { field: 'groupCount', header: 'Groups', sortable: false },
    { field: 'isActive', header: 'Status', sortable: true },
    { field: 'createdOn', header: 'Created', sortable: true },
    { field: 'actions', header: 'Actions', sortable: false }
  ]);

  readonly dialogTitle = computed(() => (this.dialogMode() === DialogMode.Create ? 'Create User' : 'Edit User'));

  readonly dialogButtonLabel = computed(() => (this.dialogMode() === DialogMode.Create ? 'Create' : 'Update'));

  readonly isLoading = computed(
    () =>
      this.usersResource.isLoading() ||
      this.accountsResource.isLoading() ||
      this.packagesResource.isLoading() ||
      this.optionsResource.isLoading()
  );

  readonly filteredUsers = computed<UserResponseV1[]>(() => {
    const searchTerm = this.searchTerm().toLowerCase();
    const users: UserResponseV1[] = this.usersResource.value() ?? [];
    if (!searchTerm) return users;

    return users.filter(
      (user: UserResponseV1) =>
        (user.name && user.name.toLowerCase().includes(searchTerm)) ||
        user.email.toLowerCase().includes(searchTerm) ||
        user.externalId?.toLowerCase().includes(searchTerm)
    );
  });

  readonly accounts = computed(() => this.accountsResource.value() ?? []);
  readonly packages = computed(() => this.packagesResource.value() ?? []);
  readonly options = computed(() => this.optionsResource.value() ?? []);

  readonly availablePackages = computed<PackageResponseV1[]>(() => {
    return this.packages().filter((pkg) => pkg.isActive);
  });

  readonly packageOptions = computed<OptionResponseV1[]>(() => {
    const packageId = this.selectedPackageId();
    if (!packageId) return [];
    return this.options().filter((opt) => opt.isActive);
  });

  readonly currentUserMemberships = computed<UserMembershipSummary | undefined>(() => {
    const summary = this.accountMembershipSummary();
    const userId = this.selectedUserForMemberships()?.userId;
    if (!summary || !userId) return undefined;
    return summary.memberships.find((u) => u.userId === userId);
  });

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
  }

  onCreateUser(): void {
    this.dialogMode.set(DialogMode.Create);
    this.selectedUserId.set(null);
    this.userFormModel.set({
      externalId: '',
      email: '',
      name: '',
      isActive: true
    });
    this.showDialog.set(true);
  }

  onEditUser(user: UserResponseV1): void {
    this.dialogMode.set(DialogMode.Edit);
    this.selectedUserId.set(user.userId);
    this.userFormModel.set({
      userId: user.userId,
      externalId: user.externalId,
      email: user.email,
      name: user.name,
      isActive: user.isActive
    });
    this.showDialog.set(true);
  }

  async onSaveUser(): Promise<void> {
    if (!this.isFormValid()) {
      this.showError('Please fix validation errors');
      return;
    }

    this.saving.set(true);
    const formData = this.userFormModel();

    const operation =
      this.dialogMode() === DialogMode.Create
        ? this.accessManagementService.createUser(formData as CreateUserRequestV1)
        : this.accessManagementService.updateUser(this.selectedUserId()!, formData as Partial<UserResponseV1>);

    try {
      await firstValueFrom(operation);
      this.showSuccess(`User ${this.dialogMode() === DialogMode.Create ? 'created' : 'updated'} successfully`);
      this.showDialog.set(false);
      this.usersResource.reload();
    } catch (error) {
      this.showError('Failed to save user');
      console.error('Save user error:', error);
    } finally {
      this.saving.set(false);
    }
  }

  onDeleteUser(user: UserResponseV1): void {
    this.userPendingDelete.set(user);
    this.showDeleteDialog.set(true);
  }

  async onConfirmDelete(mode: DeleteMode): Promise<void> {
    const user = this.userPendingDelete();
    if (!user) return;

    this.deletingId.set(user.userId);
    try {
      await firstValueFrom(this.accessManagementService.deleteUser(user.userId, mode));
      this.showSuccess(`User ${user.name} deleted successfully`);
      this.showDeleteDialog.set(false);
      this.usersResource.reload();
    } catch (error) {
      this.showError('Failed to delete user');
      console.error('Delete user error:', error);
    } finally {
      this.deletingId.set(null);
    }
  }

  onViewMemberships(user: UserResponseV1): void {
    this.selectedUserForMemberships.set(user);
    this.showMembershipsDialog.set(true);
    this.selectedAccountId.set('');
    this.accountMembershipSummary.set(null);
  }

  async onAccountChange(accountId: string): Promise<void> {
    if (!accountId) {
      this.accountMembershipSummary.set(null);
      return;
    }

    this.membershipsLoading.set(true);
    try {
      const summary = await firstValueFrom(this.membershipService.getAccountMemberships(accountId));
      this.accountMembershipSummary.set(summary);
    } catch (error) {
      this.showError('Failed to load account memberships');
      console.error('Load memberships error:', error);
      this.accountMembershipSummary.set(null);
    } finally {
      this.membershipsLoading.set(false);
    }
  }

  onOpenAllocateDialog(): void {
    this.showAllocateDialog.set(true);
    this.selectedPackageId.set('');
    this.selectedOptionId.set('');
  }

  async onAllocatePackage(): Promise<void> {
    const userId = this.selectedUserForMemberships()?.userId;
    const accountId = this.selectedAccountId();
    const packageId = this.selectedPackageId();
    const optionId = this.selectedOptionId();

    if (!userId || !accountId || !packageId || !optionId) {
      this.showError('Please select a package and option');
      return;
    }

    this.allocating.set(true);
    try {
      await firstValueFrom(
        this.membershipService.allocatePackageToUser(accountId, {
          userId,
          packageId,
          optionId
        })
      );
      this.showSuccess('Package allocated successfully');
      this.showAllocateDialog.set(false);
      // Reload memberships
      await this.onAccountChange(accountId);
    } catch (error) {
      this.showError('Failed to allocate package');
      console.error('Allocate package error:', error);
    } finally {
      this.allocating.set(false);
    }
  }

  getStatusSeverity(isActive: boolean): 'success' | 'danger' {
    return isActive ? 'success' : 'danger';
  }

  getStatusLabel(isActive: boolean): string {
    return isActive ? 'Active' : 'Inactive';
  }

  isFormValid(): boolean {
    const model = this.userFormModel();
    return !!(
      model.externalId.trim() &&
      model.email.trim() &&
      model.name.trim() &&
      this.userForm.externalId().valid() &&
      this.userForm.email().valid() &&
      this.userForm.name().valid()
    );
  }

  isAllocateFormValid(): boolean {
    return !!(this.selectedAccountId() && this.selectedPackageId() && this.selectedOptionId());
  }

  onPackageChange(): void {
    this.selectedOptionId.set('');
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }

  private showSuccess(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message
    });
  }
}
